import { createContext, useContext, useState } from "react";
import { validateToken, signOut } from "../services/authService";
import { clearVisitorCheckoutContact } from "../utils/visitorCheckoutContact";
import { useQuery } from "@tanstack/react-query";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState({
    isAuthenticated: false,
    isLoadingUser: true,
    data: null,
  });


  useQuery(["validateToken"], validateToken, {
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 0,               // Data is immediately stale
    refetchOnMount: true,       // Always refetch on mount/remount
    gcTime: 0,
    onSuccess: (data) => {
      if (data.data.success) {
        setCurrentUser((prev) => ({
          ...prev,
          isAuthenticated: true,
          isLoadingUser: false,
          data: data.data.data,
        }));
      }
    },
    onError: (error) => {
      setCurrentUser((prev) => ({
        ...prev,
        isAuthenticated: false,
        isLoadingUser: false,
        data: null,
      }));
    },
  });

  const clearAuthSession = () => {
    setCurrentUser({
      isAuthenticated: false,
      isLoadingUser: false,
      data: null,
    });
  };

  const login = (userData) => {
    setCurrentUser((prev) => ({
      ...prev,
      data: userData,
      isAuthenticated: true,
      isLoadingUser: false,
    }));
  };

  const logout = () => {
    signOut().finally(() => {
      clearVisitorCheckoutContact();
      clearAuthSession();
      window.location.replace("/");
    });
  };

  const contextValue = {
    login,
    logout,
    clearAuthSession,
    isAuthenticated: currentUser.isAuthenticated,
    currentUser: currentUser.data,
    isLoadingUser: currentUser.isLoadingUser,
    setCurrentUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
